<template>
  <v-container
    fluid
    data-cy="share-preferences-container"
  >
    <v-row no-gutters>
      <v-col class="d-flex justify-start">
        <div class="rounded-circle illustration-container mr-4">
          <sn-illustration
            illustration="CoBorrowersIncomplete"
            size="xs"
          />
        </div>
        <div class="d-flex justify-center align-start flex-column">
          <h3
            class="sn-h3 pb-2"
            data-cy="share-preferences-title"
          >
            Share preferences
          </h3>
          <p
            class="sn-body-1 pt-2 mb-0"
            data-cy="share-preferences-subtitle"
          >
            Add team members that you would like to automatically share all future files with.
          </p>
        </div>
      </v-col>
    </v-row>
    <v-row
      v-if="!showAddShare"
      no-gutters
      class="pr-6"
    >
      <v-col class="d-flex justify-end mb-4">
        <sn-btn
          text="Add"
          prepend-icon="$snPlus"
          size="large"
          data-cy="add-btn"
          @click="showAddShare = true"
        />
      </v-col>
    </v-row>
    <edit-share-contact
      v-model="showAddShare"
      class="mt-7"
      :shared-contact-emails="sharedContactEmails"
      @save="handleShare"
    />
    <template v-for="(contact) in contactsToDisplay">
      <div :key="contact.guid">
        <v-row
          v-if="!editing[contact.guid]"
          no-gutters
          :class="contactClass(contact)"
          :data-cy="contact.name ? `${contact.name.replace(/\s/g, '-').toLowerCase()}-row` : 'no-name-contact-row'"
        >
          <v-col class="d-flex flex-column">
            <div class="sn-subtitle-1">
              {{ contact.name }} <span v-if="contact.manager">(Manager)</span>
            </div>
            <div class="sn-body-1 mt-1">
              {{ contact.email }}
            </div>
          </v-col>
          <v-col class="d-flex justify-end align-center">
            <div class="d-flex align-center justify-end mr-14">
              <div
                :class="shareFilesClass(contact)"
                style="padding-bottom: 3px"
              >
                Share all files with this contact
              </div>
              <sn-switch
                :value="contact.share_all_files"
                :ripple="false"
                @change="shareAllFiles => toggleShareAllFiles(contact, shareAllFiles)"
              />
            </div>
            <v-menu
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-icon v-on="on">
                  more_vert
                </v-icon>
              </template>

              <v-list>
                <v-list-item
                  data-cy="edit-btn"
                  @click="editContact(contact)"
                >
                  <v-list-item-content>
                    Edit
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  data-cy="delete-btn"
                  @click="handleDelete(contact)"
                >
                  <v-list-item-content>
                    Delete
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
        <edit-share-contact
          v-model="editing[contact.guid]"
          class="share-contact"
          :contact-guid="contact.guid"
          :shared-contact-emails="sharedContactEmails"
          :old-name="contact.name"
          :old-email="contact.email"
          :old-manager="contact.manager"
          :currently-shared-files-count="contact.currently_shared_files_count"
          @save="handleEdit(contact, $event)"
        />
      </div>
    </template>
    <div
      v-if="showPagination"
      class="d-flex flex-row-reverse py-6"
    >
      <sn-pagination
        :pagination="pagination"
        :per-page-options="[10, 25, 50]"
        :filtered-total="sharedContacts.length"
        @pagination="updatePagination"
      />
    </div>
    <sn-modal
      v-model="showChickenOutModal"
      type="decision"
      title="Delete contact"
      data-cy="confirm-delete-contact-modal"
      primary-text="Continue"
      warning-text="This action cannot be undone"
      warning-icon="$snWarning"
      :loading="submitting"
      @submit="deleteContact"
    >
      <p>Deleting this contact will <b>revoke their access</b> to any files that you previously shared with them.</p>
      <p>Are you sure you want to proceed?</p>
    </sn-modal>
  </v-container>
</template>

<script>
import closingPortalService from '~/services/snClosingPortalService'
import editShareContact from '~/components/portals/closing/myAccount/editShareContact.vue'
import { mapMutations } from 'vuex'

export default {
  name: 'SharePreferences',

  components: {
    editShareContact
  },

  props: {
    reloadSharedContacts: {
      type: Boolean,
      default: false,
      required: true
    }
  },

  data () {
    return {
      showAddShare: false,
      sharedContacts: [],
      contactToDelete: null,
      submitting: false,
      showChickenOutModal: false,
      editing: {},
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: '',
        sortDesc: true,
        inMemoryPagination: false
      }
    }
  },

  computed: {
    headers () {
      return [
        {
          text: '',
          value: 'sharedContact',
          sortable: false
        }
      ]
    },
    sharedContactEmails () {
      return this.sharedContacts?.map(c => c.email)
    },
    contactsToDisplay () {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage
      const end = this.pagination.page * this.pagination.itemsPerPage
      return this.sharedContacts.slice(start, end)
    },
    showPagination () {
      return this.pagination.itemsPerPage < this.sharedContacts.length
    }
  },

  watch: {
    reloadSharedContacts (newVal) {
      if (newVal) {
        this.updateSharedContacts()
      }
    },
    sharedContacts () {
      if (this.contactsToDisplay.length < 1) {
        this.updatePagination({
          page: 1,
          itemsPerPage: 10,
          sortBy: '',
          sortDesc: true,
          inMemoryPagination: false
        })
      }
    }
  },

  created () {
    this.updateSharedContacts()
  },

  methods: {
    ...mapMutations(['setSuccessToast', 'setErrorToast', 'setWarningToast']),
    updateSharedContacts () {
      closingPortalService.getSharedContacts().then(resp => {
        this.sharedContacts = resp.data.closing_portal_shared_contacts
        this.sharedContacts.forEach(contact => {
          // initializing the property using $set makes changes to it reactive
          this.$set(this.editing, contact.guid, false)
        })
      }).catch(err => {
        console.error(err)
      })
    },
    contactClass (contact) {
      const base = 'share-contact d-flex justify-space-between align-center pa-5'
      if (contact.highlighted) {
        return base + ' highlighted'
      }
      return base
    },
    shareFilesClass (contact) {
      const baseClass = 'mr-4'
      if (contact.share_all_files) {
        return baseClass
      } else {
        return baseClass + ' sn-text-primary-01'
      }
    },
    handleShare (response) {
      if (!response.successful) {
        if (response.error.includes('Please only share with Closing Portal users.')) {
          this.setErrorToast({
            message: 'Email not recognized',
            description: response.error
          })
        } else {
          this.setErrorToast({
            description: response.error
          })
        }
        return
      }

      const contact = response.closing_portal_shared_contact
      contact.highlighted = true
      this.insertContact(contact)

      this.setSuccessToast({
        message: 'Contact added',
        description: 'Changes will apply to all future files.'
      })
      // Remove the highlighted style after 3 seconds.
      setTimeout(() => {
        const highlightedContactIdx = this.sharedContacts.indexOf(contact)
        const highlightedContact = this.sharedContacts[highlightedContactIdx]
        highlightedContact.highlighted = false
        this.$set(this.sharedContacts, highlightedContactIdx, highlightedContact)
      }, 500)
    },
    toggleShareAllFiles (contact, shareAllFiles) {
      closingPortalService.updateClosingPortalSharedContactPreferences(contact.guid, shareAllFiles)
        .then(({ data: { update_closing_portal_shared_contact_preferences: response } }) => {
          if (response.successful) {
            this.setSuccessToast({
              message: `Share turned "${shareAllFiles ? 'ON' : 'OFF'}"`,
              description: 'Changes will apply to all future files.'
            })

            const spliceIdx = this.sharedContacts.findIndex(c => c.guid === contact.guid)
            this.sharedContacts.splice(spliceIdx, 1, response.closing_portal_shared_contact)
          } else {
            this.setErrorToast({
              message: 'Error changing sharing preferences',
              description: response.error
            })
          }
        })
    },
    editContact (contact) {
      this.editing[contact.guid] = true
    },
    insertContact (contact) {
      this.sharedContacts.unshift(contact)
      this.$set(this.editing, contact.guid, false)
    },
    handleEdit (oldContact, response) {
      const contact = response.closing_portal_shared_contact
      if (response.successful) {
        this.setSuccessToast({
          message: 'Contact edited',
          description: 'Changes will apply to all future files'
        })

        const spliceIndex = this.sharedContacts.findIndex(c => c.guid === oldContact.guid)
        this.sharedContacts.splice(spliceIndex, 1, contact)
      } else {
        this.setErrorToast({
          message: 'Error changing sharing preferences',
          description: response.error
        })
      }
    },
    handleDelete (contact) {
      this.submitting = false
      this.contactToDelete = contact
      this.showChickenOutModal = !this.showChickenOutModal
    },
    deleteContact () {
      this.submitting = true
      closingPortalService.deleteSharedContact(this.contactToDelete?.guid).then(resp => {
        if (!resp?.data?.delete_closing_portal_shared_contact?.successful) {
          this.setErrorToast({
            message: 'Something went wrong',
            description: resp?.data?.delete_closing_portal_shared_contact?.error
          })
          this.showChickenOutModal = false
          this.contactToDelete = null
          return
        }
        this.setSuccessToast({
          message: 'Contact deleted',
          description: 'Contact no longer has access to previously shared files.'
        })
        this.showChickenOutModal = false
        this.updateSharedContacts()
      }).catch(err => {
        console.error(err)
        this.setErrorToast({
          message: 'Something went wrong',
          description: err
        })
      })
    },
    updatePagination (value) {
      this.pagination = value
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui/index.scss';
::v-deep {
  .v-data-table-header {
    display: none;
  }
}

.highlighted {
  background-color: $sn-brand-01;
}

.illustration-container {
  background-color: $sn-brand-01;
  width: 112px;
  height: 112px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.share-contact {
  &:first-child {
    border-top: 1px solid $sn-gray-03;
  }
  border-bottom: 1px solid $sn-gray-03;
  transition: background-color 1s ease-out;
}
</style>
